import { CURRENCY, Shipment } from "../reducers/uploadedBoard";

export const dateToHumanReadableDate = (date: Date, dayOfWeek?: boolean) => {
  return date.toLocaleDateString(undefined, {
    year: dayOfWeek ? undefined : "numeric",
    weekday: dayOfWeek ? "short" : undefined,
    month: "short",
    day: "numeric",
  });
};

export const dateToHumanReadableDatetime = (date: Date) => {
  return date.toLocaleTimeString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

export const dateStringToDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split("-");
  return new Date(parseInt(year!), parseInt(month!) - 1, parseInt(day!));
};

export const dateStringToHumanReadableDate = (
  dateString: string,
  dayOfWeek?: boolean,
) => {
  return dateToHumanReadableDate(dateStringToDate(dateString), dayOfWeek);
};

export const dateInputStringToDate = (dateString: string): Date => {
  const [month, day, year] = dateString.split("/");
  return new Date(parseInt(year!), parseInt(month!) - 1, parseInt(day!));
};

export const dateInputStringToHumanReadableDate = (
  dateString: string,
  dayOfWeek?: boolean,
) => {
  return dateToHumanReadableDate(dateInputStringToDate(dateString), dayOfWeek);
};

export const datetimeStringToHumanReadableDate = (datetimeString: string) => {
  return dateToHumanReadableDate(new Date(datetimeString));
};

export const datetimeStringToHumanReadableDatetime = (
  datetimeString: string,
) => {
  const date = new Date(datetimeString);
  return dateToHumanReadableDatetime(date);
};

export const datetimeToTimestampStringForFilename = (date: Date): string => {
  return date.toISOString().replace(/[:.]/g, "-").slice(0, -5);
};

const getCurrencyFormatter = (currency: CURRENCY, small: boolean = false) => {
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency,
    compactDisplay: "long",
    maximumFractionDigits: small ? 5 : 2,
    minimumFractionDigits: 2,
  });
};

export const datetimeStringToUTCDatetimeString = (datetimeString: string) => {
  const date = new Date(datetimeString);
  const now_utc = Date.UTC(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );

  const utcDate = new Date(now_utc);
  return utcDate.toISOString();
};

export const dateToLocalDateString = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0"); // Months are 0-based
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

export function fromLocalDateString(dateString: string) {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year!, month! - 1, day!); // month is 0-indexed
}

export const dateToDatetimeString = (date: Date) => {
  return date.toISOString();
};

export const todayInPhillyToDateInputString = (plusDays: number = 0) => {
  const today = new Date();
  today.setDate(today.getDate() + plusDays);
  return dateToLocalDateString(today);
};

export const formatCurrency = (amount: number, currency: CURRENCY) => {
  return getCurrencyFormatter(currency).format(
    Math.ceil(amount * 100) / 100 == 0 ? 0 : amount,
  );
};

export const formatSmallCurrency = (amount: number, currency: CURRENCY) => {
  return getCurrencyFormatter(currency, true).format(amount);
};

const removeNonNumericDigitsFromString = (string: string) =>
  string.replace(/\D/g, "");

export const formatPhoneNumberNoCountryCode = (phoneNumber: string) =>
  phoneNumber.slice(2);

export const formatReadablePhoneNumber = (phoneNumber: string) => {
  const splitPhoneNumber = phoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/);

  return splitPhoneNumber
    ? `(${splitPhoneNumber[1]})-${splitPhoneNumber[2]}-${splitPhoneNumber[3]}`
    : null;
};

export const formatReadablePhoneNumberWithCountryCode = (
  phoneNumber: string,
) => {
  return formatReadablePhoneNumber(formatPhoneNumberNoCountryCode(phoneNumber));
};

export const formatPhoneNumberInput = (
  phoneNumber: string,
  isOrderingBoard?: boolean,
) =>
  phoneNumber
    ? `${isOrderingBoard ? "" : "+1"}${removeNonNumericDigitsFromString(
        phoneNumber,
      )}`
    : "";

export const isInvalidPhoneNumber = (phoneNumber: string) =>
  removeNonNumericDigitsFromString(phoneNumber).length != 10 &&
  phoneNumber != "";

export const formatStringForPath = (string: string) => {
  const separatedString = string
    .replace(/\s+/g, "-")
    .replace(/_/g, "-")
    .split("-");

  let path = "";
  separatedString.forEach((string, index) => {
    const noSpecialCharacters = string.replace(/\W/g, "");
    if (index == 0) {
      path += noSpecialCharacters;
    } else {
      path += `-${noSpecialCharacters}`;
    }
  });
  return path;
};

export const leadTimeInfoString = (
  days: number,
  shipDate?: string | null,
  disabled?: boolean,
) => {
  if (disabled) {
    return `${days} days (currently unavailable)`;
  }
  if (shipDate) {
    return `${days} days (ships ${dateStringToHumanReadableDate(
      shipDate,
      true,
    )})`;
  }
  return `${days} days`;
};

export function numberToWords(num: number) {
  if (num === 0) return "zero";

  const belowTwenty = [
    "one",
    "two",
    "three",
    "four",
    "five",
    "six",
    "seven",
    "eight",
    "nine",
    "ten",
    "eleven",
    "twelve",
    "thirteen",
    "fourteen",
    "fifteen",
    "sixteen",
    "seventeen",
    "eighteen",
    "nineteen",
  ];
  const tens = [
    "twenty",
    "thirty",
    "forty",
    "fifty",
    "sixty",
    "seventy",
    "eighty",
    "ninety",
  ];
  const thousands = ["", "thousand", "million", "billion", "trillion"];

  function helper(num: number): string {
    if (num === 0) {
      return "";
    } else if (num < 20) {
      return belowTwenty[num - 1] + " ";
    } else if (num < 100) {
      return tens[Math.floor(num / 10) - 2] + " " + helper(num % 10);
    }
    return (
      belowTwenty[Math.floor(num / 100) - 1] + " hundred " + helper(num % 100)
    );
  }

  let word = "";
  let i = 0;

  while (num > 0) {
    if (num % 1000 !== 0) {
      word = helper(num % 1000) + thousands[i] + " " + word;
    }
    num = Math.floor(num / 1000);
    i++;
  }

  return word.trim();
}

export function twentyFourHourToTwelveHour(hours: number) {
  if (hours > 24 || hours < 0) {
    throw Error(`Should be less than or equal to 24, is ${hours}`);
  }
  const period = hours >= 12 ? "pm" : "am";
  // Adjust hours for 12-hour format
  const hours12 = hours % 12 || 12;
  // Format the time string
  return `${hours12}${period}`;
}

function toCoordinateString(dim: number) {
  return dim.toLocaleString(undefined, {
    maximumFractionDigits: 3,
    minimumFractionDigits: 3,
  });
}

export function xyformatting(x: number, y: number, unit: string): string {
  return `x: ${toCoordinateString(x)} ${unit}, y: ${toCoordinateString(y)} ${unit}`;
}

function formatLeadTimeDescriptor(shipment: Shipment) {
  if (shipment.ship_date && shipment.delivery_info.shipping_address) {
    return `shipping ${dateStringToHumanReadableDate(shipment.ship_date)}`;
  } else if (shipment.ship_date && shipment.delivery_info.pickup_info) {
    return `pick up on ${dateStringToHumanReadableDate(shipment.ship_date)}`;
  } else if (shipment.lead_time_days) {
    return `${shipment.lead_time_days} days lead time`;
  } else {
    return `${shipment.base_lead_time_days} days lead time`;
  }
}

export function formatShipmentLine(shipment: Shipment) {
  return `${shipment.num_boards.toLocaleString()} boards (${formatLeadTimeDescriptor(shipment)})`;
}
